import React, { useState, useEffect, useRef } from "react";
import ReactMarkdown from "react-markdown";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { invoicesAPI, clientsAPI, quotesAPI, timeEntriesAPI, paymentsAPI, savingsAPI } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatCurrency, formatDate, formatDuration, getStatusColor, INVOICE_STATUSES } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Printer, Receipt, Send, DollarSign, Clock, FileText } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full logo URL
const getLogoUrl = (logoUrl) => {
  if (!logoUrl) return null;
  if (logoUrl.startsWith("/api")) {
    return `${BACKEND_URL}${logoUrl}`;
  }
  return logoUrl;
};

export default function InvoiceDetailPage() {
  const { invoiceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const printRef = useRef();
  
  const isNew = invoiceId === "new";
  const clientIdParam = searchParams.get("client_id");
  const quoteIdParam = searchParams.get("quote_id");

  const [invoice, setInvoice] = useState(null);
  const [client, setClient] = useState(null);
  const [clients, setClients] = useState([]);
  const [payments, setPayments] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [unbilledEntries, setUnbilledEntries] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    client_id: clientIdParam || "",
    quote_id: quoteIdParam || "",
    title: "",
    notes: "",
    due_date: "",
    lines: [],
  });

  // Dialogs
  const [showLineDialog, setShowLineDialog] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showTimeEntriesDialog, setShowTimeEntriesDialog] = useState(false);
  const [selectedTimeEntries, setSelectedTimeEntries] = useState([]);
  const [showCommissionDialog, setShowCommissionDialog] = useState(false);
  const [commissionForm, setCommissionForm] = useState({
    description: "",
    base_amount: "",
    commission_pct: "10",
  });

  const [lineForm, setLineForm] = useState({
    description: "",
    quantity: "1",
    unit_price: "",
  });

  const [paymentForm, setPaymentForm] = useState({
    invoice_amount: "",
    received_amount: "",
    received_currency: "",
    exchange_rate: "",
    base_amount: "",
    fees: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "",
    notes: "",
    include_in_accountant_reports: true,
    deposit_account_id: "",
    user_confirmed_balance_after: "",
  });
  const [accounts, setAccounts] = useState([]);
  const [balancePreview, setBalancePreview] = useState(null);

  useEffect(() => {
    if (isNew) {
      fetchClients();
      if (clientIdParam) {
        fetchClientData(clientIdParam);
      }
      if (quoteIdParam) {
        loadFromQuote(quoteIdParam);
      }
    } else {
      fetchInvoice();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId, clientIdParam, quoteIdParam]);

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.list({ status: "active" });
      setClients(res.data);
    } catch (err) {
      console.error("Error fetching clients:", err);
    }
  };

  const fetchClientData = async (clientId) => {
    try {
      const [clientRes, timeRes, quotesRes] = await Promise.all([
        clientsAPI.get(clientId),
        timeEntriesAPI.list({ client_id: clientId, billable: true }),
        quotesAPI.list({ client_id: clientId, status: "approved" }),
      ]);
      setClient(clientRes.data);
      setUnbilledEntries(timeRes.data);
      setQuotes(quotesRes.data);
    } catch (err) {
      console.error("Error fetching client data:", err);
    }
  };

  const fetchInvoice = async () => {
    try {
      const res = await invoicesAPI.get(invoiceId);
      setInvoice(res.data);
      setFormData({
        client_id: res.data.client_id,
        quote_id: res.data.quote_id || "",
        title: res.data.title,
        notes: res.data.notes || "",
        due_date: res.data.due_date || "",
        lines: res.data.lines || [],
      });
      
      // Fetch related data
      const [clientRes, paymentsRes] = await Promise.all([
        clientsAPI.get(res.data.client_id),
        paymentsAPI.list({ invoice_id: invoiceId }),
      ]);
      setClient(clientRes.data);
      setPayments(paymentsRes.data);
    } catch (err) {
      toast.error("Invoice not found");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const loadFromQuote = async (quoteId) => {
    try {
      const res = await quotesAPI.get(quoteId);
      const quote = res.data;
      setFormData({
        ...formData,
        client_id: quote.client_id,
        quote_id: quoteId,
        title: `Invoice for ${quote.title}`,
        lines: quote.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          amount: l.amount,
        })),
      });
      fetchClientData(quote.client_id);
      toast.success("Loaded from quote");
    } catch (err) {
      toast.error("Failed to load quote");
    }
  };

  const handleClientChange = (clientId) => {
    setFormData({ ...formData, client_id: clientId, lines: [] });
    fetchClientData(clientId);
  };

  const handleAddLine = () => {
    const qty = parseFloat(lineForm.quantity) || 0;
    const price = parseFloat(lineForm.unit_price) || 0;
    if (!lineForm.description || price <= 0) {
      toast.error("Please fill in description and price");
      return;
    }
    const newLine = {
      ...lineForm,
      id: `temp-${Date.now()}`,
      quantity: qty,
      unit_price: price,
      amount: qty * price,
    };
    setFormData({
      ...formData,
      lines: [...formData.lines, newLine],
    });
    setLineForm({ description: "", quantity: "1", unit_price: "" });
    setShowLineDialog(false);
  };

  const handleRemoveLine = (index) => {
    const newLines = formData.lines.filter((_, i) => i !== index);
    setFormData({ ...formData, lines: newLines });
  };

  const handleAddTimeEntries = () => {
    if (selectedTimeEntries.length === 0) {
      toast.error("Please select at least one time entry");
      return;
    }

    const rate = client?.default_rate || 0;
    const newLines = selectedTimeEntries.map(entry => ({
      id: `temp-${Date.now()}-${entry.id}`,
      description: entry.description || `Time entry: ${formatDate(entry.start_time)}`,
      quantity: Math.round((entry.duration_minutes / 60) * 100) / 100,
      unit_price: rate,
      amount: Math.round((entry.duration_minutes / 60) * rate * 100) / 100,
      time_entry_ids: [entry.id],
    }));

    setFormData({
      ...formData,
      lines: [...formData.lines, ...newLines],
    });

    // Remove selected entries from unbilled list
    setUnbilledEntries(unbilledEntries.filter(e => !selectedTimeEntries.some(s => s.id === e.id)));
    setSelectedTimeEntries([]);
    setShowTimeEntriesDialog(false);
    toast.success("Time entries added to invoice");
  };

  const handleAddCommission = () => {
    const base = parseFloat(commissionForm.base_amount) || 0;
    const pct = parseFloat(commissionForm.commission_pct) || 0;
    if (!commissionForm.description || base <= 0 || pct <= 0) {
      toast.error("Please fill in all commission fields");
      return;
    }
    const amount = Math.round(base * (pct / 100) * 100) / 100;
    const newLine = {
      id: `temp-${Date.now()}`,
      description: commissionForm.description,
      quantity: 1,
      unit_price: amount,
      amount,
    };
    setFormData({ ...formData, lines: [...formData.lines, newLine] });
    setCommissionForm({ description: "", base_amount: "", commission_pct: "10" });
    setShowCommissionDialog(false);
    toast.success("Commission line added");
  };

  const handleAddMonthlyFee = () => {
    const monthLabel = new Date().toLocaleString("default", { month: "long", year: "numeric" });
    const newLine = {
      id: `temp-${Date.now()}`,
      description: `Monthly service fee — ${monthLabel}`,
      quantity: 1,
      unit_price: client?.monthly_rate || 0,
      amount: client?.monthly_rate || 0,
    };
    setFormData({ ...formData, lines: [...formData.lines, newLine] });
    toast.success("Monthly fee added");
  };

  const calculateTotal = () => {
    return formData.lines.reduce((sum, line) => sum + (line.quantity * line.unit_price), 0);
  };

  const handleSave = async () => {
    if (!formData.client_id || !formData.title || formData.lines.length === 0) {
      toast.error("Please fill in all required fields and add at least one line");
      return;
    }

    setSaving(true);
    try {
      const data = {
        client_id: formData.client_id,
        quote_id: formData.quote_id || null,
        title: formData.title,
        notes: formData.notes || null,
        due_date: formData.due_date || null,
        lines: formData.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          time_entry_ids: l.time_entry_ids || null,
        })),
      };

      if (isNew) {
        const res = await invoicesAPI.create(data);
        toast.success("Invoice created");
        navigate(`/invoices/${res.data.id}`);
      } else {
        await invoicesAPI.update(invoiceId, data);
        toast.success("Invoice updated");
        fetchInvoice();
      }
    } catch (err) {
      toast.error("Failed to save invoice");
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await invoicesAPI.update(invoiceId, { status: newStatus });
      toast.success(`Invoice marked as ${newStatus}`);
      fetchInvoice();
    } catch (err) {
      toast.error("Failed to update status");
    }
  };

  const handleAddPayment = async () => {
    const baseCurrency = user?.default_currency || "PHP";
    const invoiceCurrency = invoice?.currency || client?.currency || "PHP";

    const invoiceAmt = parseFloat(paymentForm.invoice_amount);
    if (!invoiceAmt || invoiceAmt <= 0) {
      toast.error("Please enter a valid invoice amount");
      return;
    }

    // Overpayment check (frontend guard)
    if (invoiceAmt > balance + 1e-9) {
      toast.error("Payment exceeds remaining invoice balance");
      return;
    }

    const receivedCurrency = paymentForm.received_currency || invoiceCurrency;
    const receivedAmt = parseFloat(paymentForm.received_amount) || undefined;
    const isForeign = receivedCurrency !== invoiceCurrency;

    // Require base_amount OR exchange_rate for foreign currency
    const hasBaseAmt = paymentForm.base_amount !== "" && parseFloat(paymentForm.base_amount) > 0;
    const hasRate = paymentForm.exchange_rate !== "" && parseFloat(paymentForm.exchange_rate) > 0;
    if (isForeign && !hasBaseAmt && !hasRate) {
      toast.error("For foreign currency payments, provide either Exchange Rate or Base Amount");
      return;
    }

    const payload = {
      invoice_id: invoiceId,
      invoice_amount: invoiceAmt,
      invoice_currency: invoiceCurrency,
      received_amount: receivedAmt,
      received_currency: receivedCurrency,
      exchange_rate: hasRate ? parseFloat(paymentForm.exchange_rate) : undefined,
      base_currency: baseCurrency,
      base_amount: hasBaseAmt ? parseFloat(paymentForm.base_amount) : undefined,
      fees: parseFloat(paymentForm.fees) || 0,
      payment_date: paymentForm.payment_date,
      payment_method: paymentForm.payment_method,
      notes: paymentForm.notes,
      include_in_accountant_reports: paymentForm.include_in_accountant_reports,
      deposit_account_id: paymentForm.deposit_account_id || undefined,
      user_confirmed_balance_after: paymentForm.user_confirmed_balance_after !== "" ? parseFloat(paymentForm.user_confirmed_balance_after) : undefined,
    };

    try {
      await paymentsAPI.create(payload);
      toast.success("Payment recorded");
      setShowPaymentDialog(false);
      setBalancePreview(null);
      setPaymentForm({
        invoice_amount: "",
        received_amount: "",
        received_currency: "",
        exchange_rate: "",
        base_amount: "",
        fees: "",
        payment_date: new Date().toISOString().split("T")[0],
        payment_method: "",
        notes: "",
        include_in_accountant_reports: true,
        deposit_account_id: "",
        user_confirmed_balance_after: "",
      });
      fetchInvoice();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record payment");
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm("Delete this payment?")) return;
    try {
      await paymentsAPI.delete(paymentId);
      toast.success("Payment deleted");
      fetchInvoice();
    } catch (err) {
      toast.error("Failed to delete payment");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  const total = calculateTotal();
  const paidAmount = invoice?.paid_amount || 0;
  const balance = total - paidAmount;
  const currency = invoice?.currency || client?.currency || "PHP";
  const baseCurrency = user?.default_currency || "PHP";
  const invoiceCurrency = currency;
  const clientBillingModes = client?.billing_modes || ["hourly"];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="invoice-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between no-print">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {isNew ? "New Invoice" : `Invoice ${invoice?.invoice_number}`}
            </h1>
            {!isNew && invoice && (
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusColor(invoice.status)}>{invoice.status}</Badge>
                <span className="text-muted-foreground">•</span>
                <span className="text-muted-foreground">{client?.name}</span>
              </div>
            )}
          </div>
        </div>
        <div className="flex gap-2">
          {!isNew && invoice?.status === "draft" && (
            <Button variant="outline" onClick={() => handleStatusChange("sent")}>
              <Send className="h-4 w-4 mr-2" />
              Mark Sent
            </Button>
          )}
          {!isNew && invoice?.status !== "paid" && (
            <Button variant="outline" onClick={() => setShowPaymentDialog(true)}>
              <DollarSign className="h-4 w-4 mr-2" />
              Record Payment
            </Button>
          )}
          <Button variant="outline" onClick={handlePrint}>
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      {/* Print-ready Invoice */}
      <div ref={printRef} className="invoice-container print:shadow-none">
        {/* Header */}
        <div className="invoice-header">
          <div>
            {user?.logo_url && (
              <img src={getLogoUrl(user.logo_url)} alt="Logo" className="invoice-logo mb-4" />
            )}
            <h2 className="text-2xl font-bold">{user?.business_name || user?.name}</h2>
            {user?.address && <p className="text-sm text-muted-foreground whitespace-pre-line">{user.address}</p>}
            {user?.phone && <p className="text-sm text-muted-foreground">{user.phone}</p>}
            {user?.email && <p className="text-sm text-muted-foreground">{user.email}</p>}
            {user?.tax_id && <p className="text-sm text-muted-foreground">Tax ID: {user.tax_id}</p>}
          </div>
          <div className="invoice-meta">
            <h1 className="text-3xl font-bold text-muted-foreground mb-2">INVOICE</h1>
            {!isNew && <p className="font-mono text-lg">{invoice?.invoice_number}</p>}
            <p className="text-sm text-muted-foreground mt-2">
              Date: {formatDate(invoice?.created_at || new Date().toISOString())}
            </p>
            {formData.due_date && (
              <p className="text-sm text-muted-foreground">
                Due: {formatDate(formData.due_date)}
              </p>
            )}
          </div>
        </div>

        {/* Client Info */}
        <div className="mb-8">
          <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Bill To</p>
          {isNew && !client ? (
            <div className="no-print space-y-4">
              <Select value={formData.client_id} onValueChange={handleClientChange}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {formData.client_id && quotes.length > 0 && (
                <div>
                  <Label>Load from approved quote</Label>
                  <Select onValueChange={(qid) => loadFromQuote(qid)}>
                    <SelectTrigger className="w-64 mt-1">
                      <SelectValue placeholder="Select quote" />
                    </SelectTrigger>
                    <SelectContent>
                      {quotes.map((q) => (
                        <SelectItem key={q.id} value={q.id}>
                          {q.quote_number} - {q.title} ({formatCurrency(q.total, currency)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="font-semibold text-lg">{client?.name}</p>
              {client?.address && <p className="text-sm text-muted-foreground">{client.address}</p>}
              {client?.email && <p className="text-sm text-muted-foreground">{client.email}</p>}
            </div>
          )}
        </div>

        {/* Invoice Title */}
        <div className="mb-6 no-print">
          <Label>Invoice Title *</Label>
          <Input
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            placeholder="e.g., January 2026 Services"
            className="mt-1"
          />
        </div>
        {!isNew && (
          <div className="mb-6 print-only">
            <p className="font-semibold">{formData.title}</p>
          </div>
        )}

        {/* Line Items */}
        <div className="mb-6">
          <div className="flex justify-between items-center mb-4 no-print">
            <h3 className="font-semibold">Line Items</h3>
            <div className="flex gap-2">
              {isNew && clientBillingModes.includes("hourly") && unbilledEntries.length > 0 && (
                <Button variant="outline" onClick={() => setShowTimeEntriesDialog(true)}>
                  <Clock className="h-4 w-4 mr-2" />
                  Add Time Entries ({unbilledEntries.length})
                </Button>
              )}
              {client && clientBillingModes.includes("monthly") && (
                <Button variant="outline" onClick={handleAddMonthlyFee}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Monthly Fee
                </Button>
              )}
              {client && clientBillingModes.includes("commission") && (
                <Button variant="outline" onClick={() => setShowCommissionDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Commission
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowLineDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Line
              </Button>
            </div>
          </div>

          <table className="w-full invoice-table">
            <thead>
              <tr>
                <th className="rounded-l-lg">Description</th>
                <th className="text-right">Qty</th>
                <th className="text-right">Rate</th>
                <th className="text-right rounded-r-lg">Amount</th>
                <th className="w-12 no-print"></th>
              </tr>
            </thead>
            <tbody>
              {formData.lines.map((line, index) => (
                <tr key={line.id || index}>
                  <td>{line.description}</td>
                  <td className="text-right font-mono">{line.quantity}</td>
                  <td className="text-right font-mono">{formatCurrency(line.unit_price, currency)}</td>
                  <td className="text-right font-mono">{formatCurrency(line.quantity * line.unit_price, currency)}</td>
                  <td className="no-print">
                    <Button variant="ghost" size="icon" onClick={() => handleRemoveLine(index)} className="h-8 w-8 text-destructive">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
              {formData.lines.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-center text-muted-foreground py-8">
                    No line items yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Totals */}
        <div className="invoice-totals">
          <div className="invoice-total-row">
            <span>Subtotal</span>
            <span className="font-mono">{formatCurrency(total, currency)}</span>
          </div>
          {!isNew && paidAmount > 0 && (
            <div className="invoice-total-row text-green-600">
              <span>Paid</span>
              <span className="font-mono">-{formatCurrency(paidAmount, currency)}</span>
            </div>
          )}
          <div className="invoice-grand-total">
            <span>{!isNew && paidAmount > 0 ? "Balance Due" : "Total"}</span>
            <span className="font-mono">{formatCurrency(!isNew ? balance : total, currency)}</span>
          </div>
        </div>

        {/* Bank Details */}
        {user?.bank_details && (
          <div className="mt-8 p-4 bg-muted rounded-lg print:bg-gray-100 print:text-black">
            <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2 print:text-gray-600">Payment Details</p>
            <div className="text-sm [&_a]:text-blue-600 [&_a]:underline [&_img]:max-w-full [&_img]:h-auto print:text-black">
              <ReactMarkdown
                components={{
                  a: ({ node, children, href, ...props }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      {...props}
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {user.bank_details}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {/* Notes */}
        <div className="mt-8">
          <Label className="no-print">Notes</Label>
          <Textarea
            value={formData.notes}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Additional notes or terms..."
            rows={3}
            className="mt-1 no-print"
          />
          {formData.notes && (
            <div className="print-only mt-4 p-4 bg-muted rounded-lg print:bg-gray-100 print:text-black">
              <p className="text-sm whitespace-pre-line">{formData.notes}</p>
            </div>
          )}
        </div>

        {/* Due Date */}
        <div className="mt-4 no-print">
          <Label>Due Date</Label>
          <Input
            type="date"
            value={formData.due_date}
            onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
            className="mt-1 w-48"
          />
        </div>

        {/* Print-only branding footer */}
        <div className="print-only mt-8 pt-4 border-t border-gray-200 flex justify-end">
          <a
            href="https://plotsandpages.store"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none", color: "#bbb", fontSize: "9px", fontFamily: "Inter, sans-serif" }}
          >
            Powered by LJ3K
          </a>
        </div>
      </div>

      {/* Payments Section */}
      {!isNew && payments.length > 0 && (
        <Card className="no-print">
          <CardHeader>
            <CardTitle>Payment History</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Applied ({invoiceCurrency})</TableHead>
                  <TableHead>Received</TableHead>
                  <TableHead>Base ({baseCurrency})</TableHead>
                  <TableHead>Method</TableHead>
                  <TableHead>Notes</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {payments.map((payment) => {
                  const isLegacy = payment.invoice_amount == null && payment.amount != null;
                  const appliedAmt = payment.invoice_amount ?? payment.amount;
                  return (
                    <TableRow key={payment.id}>
                      <TableCell>
                        {formatDate(payment.payment_date)}
                        {isLegacy && (
                          <span className="ml-1 text-xs text-muted-foreground">(legacy)</span>
                        )}
                      </TableCell>
                      <TableCell className="font-mono text-green-600">
                        {formatCurrency(appliedAmt, invoiceCurrency)}
                      </TableCell>
                      <TableCell className="font-mono">
                        {payment.received_amount != null
                          ? `${formatCurrency(payment.received_amount, payment.received_currency || invoiceCurrency)}`
                          : "-"}
                      </TableCell>
                      <TableCell className="font-mono">
                        {payment.base_amount != null
                          ? formatCurrency(payment.base_amount, payment.base_currency || baseCurrency)
                          : "-"}
                      </TableCell>
                      <TableCell>{payment.payment_method || "-"}</TableCell>
                      <TableCell>{payment.notes || "-"}</TableCell>
                      <TableCell>
                        <Button variant="ghost" size="icon" onClick={() => handleDeletePayment(payment.id)} className="h-8 w-8 text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Add Line Dialog */}
      <Dialog open={showLineDialog} onOpenChange={setShowLineDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Line Item</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={lineForm.description}
                onChange={(e) => setLineForm({ ...lineForm, description: e.target.value })}
                placeholder="Service or item description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={lineForm.quantity}
                  onChange={(e) => setLineForm({ ...lineForm, quantity: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Unit Price *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={lineForm.unit_price}
                  onChange={(e) => setLineForm({ ...lineForm, unit_price: e.target.value })}
                />
              </div>
            </div>
            <div className="text-right text-lg font-semibold">
              Amount: {formatCurrency((parseFloat(lineForm.quantity) || 0) * (parseFloat(lineForm.unit_price) || 0), currency)}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLineDialog(false)}>Cancel</Button>
            <Button onClick={handleAddLine}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Payment Dialog */}
      <Dialog
        open={showPaymentDialog}
        onOpenChange={(open) => {
          if (open) {
            // Set sensible defaults when opening
            const defRecvCurrency = invoiceCurrency;
            setPaymentForm((prev) => ({
              ...prev,
              received_currency: defRecvCurrency,
              // Pre-fill received_amount with balance only when same currency
              received_amount: defRecvCurrency === invoiceCurrency ? String(Math.max(0, balance)) : "",
              invoice_amount: defRecvCurrency === invoiceCurrency ? String(Math.max(0, balance)) : "",
            }));
            // Load accounts for deposit account selector
            savingsAPI.list().then((res) => setAccounts(res.data || [])).catch(() => {});
          } else {
            setBalancePreview(null);
          }
          setShowPaymentDialog(open);
        }}
      >
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Receive Payment</DialogTitle>
            <DialogDescription>
              Balance due: {formatCurrency(balance, invoiceCurrency)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Invoice Amount */}
            <div className="space-y-2">
              <Label>Invoice Amount ({invoiceCurrency}) *</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.invoice_amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, invoice_amount: e.target.value })}
                placeholder={`Amount in ${invoiceCurrency}`}
              />
            </div>

            {/* Received Currency */}
            <div className="space-y-2">
              <Label>Received Currency *</Label>
              <Select
                value={paymentForm.received_currency || invoiceCurrency}
                onValueChange={(val) => {
                  const prev = paymentForm.received_currency || invoiceCurrency;
                  // If switching away from invoice currency to base, clear received_amount
                  const newReceivedAmt =
                    val === invoiceCurrency
                      ? paymentForm.received_amount
                      : prev === invoiceCurrency
                      ? ""
                      : paymentForm.received_amount;
                  setPaymentForm({
                    ...paymentForm,
                    received_currency: val,
                    received_amount: newReceivedAmt,
                    // Clear FX fields when going back to same currency
                    exchange_rate: val === invoiceCurrency ? "" : paymentForm.exchange_rate,
                    base_amount: val === invoiceCurrency ? "" : paymentForm.base_amount,
                  });
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[...new Set([invoiceCurrency, baseCurrency, "USD", "CAD", "EUR"].filter(Boolean))].map((c) => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Received Amount */}
            <div className="space-y-2">
              <Label>Received Amount ({paymentForm.received_currency || invoiceCurrency})</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.received_amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, received_amount: e.target.value })}
                placeholder={`Amount received in ${paymentForm.received_currency || invoiceCurrency}`}
              />
            </div>

            {/* FX fields — only when received currency differs from invoice currency */}
            {paymentForm.received_currency && paymentForm.received_currency !== invoiceCurrency && (
              <>
                <div className="space-y-2">
                  <Label>Exchange Rate (1 {paymentForm.received_currency} = ? {baseCurrency})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.000001"
                    value={paymentForm.exchange_rate}
                    onChange={(e) => setPaymentForm({ ...paymentForm, exchange_rate: e.target.value })}
                    placeholder="e.g. 56.5"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Base Amount ({baseCurrency})</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={paymentForm.base_amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, base_amount: e.target.value })}
                    placeholder={`Amount in ${baseCurrency}`}
                  />
                </div>
              </>
            )}

            {/* Fees */}
            <div className="space-y-2">
              <Label>Fees ({baseCurrency})</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={paymentForm.fees}
                onChange={(e) => setPaymentForm({ ...paymentForm, fees: e.target.value })}
                placeholder="0.00"
              />
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label>Payment Date *</Label>
              <Input
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
              />
            </div>

            {/* Payment Method */}
            <div className="space-y-2">
              <Label>Payment Method</Label>
              <Input
                value={paymentForm.payment_method}
                onChange={(e) => setPaymentForm({ ...paymentForm, payment_method: e.target.value })}
                placeholder="e.g., Bank Transfer, PayPal"
              />
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                rows={2}
              />
            </div>

            {/* Include in accountant reports */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="include_in_accountant_reports"
                checked={paymentForm.include_in_accountant_reports}
                onCheckedChange={(checked) =>
                  setPaymentForm({ ...paymentForm, include_in_accountant_reports: !!checked })
                }
              />
              <Label htmlFor="include_in_accountant_reports" className="cursor-pointer">
                Include in accountant reports
              </Label>
            </div>

            {/* Deposit Account (optional) */}
            {accounts.length > 0 && (
              <div className="space-y-2 border-t border-border pt-4">
                <Label>Deposit to Account (optional)</Label>
                <Select
                  value={paymentForm.deposit_account_id || "none"}
                  onValueChange={async (val) => {
                    const accountId = val === "none" ? "" : val;
                    setPaymentForm((p) => ({ ...p, deposit_account_id: accountId, user_confirmed_balance_after: "" }));
                    setBalancePreview(null);
                    if (accountId) {
                      const amt = parseFloat(paymentForm.invoice_amount) || 0;
                      if (amt > 0) {
                        try {
                          const previewRes = await savingsAPI.getBalancePreview(accountId, "inflow", amt);
                          setBalancePreview(previewRes.data);
                        } catch { /* ignore */ }
                      }
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select account..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No account update</SelectItem>
                    {accounts.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.account_label || a.bank_name} ({formatCurrency(a.current_balance ?? a.amount ?? 0, a.currency)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {balancePreview && (
                  <div className="rounded-md bg-muted/40 p-3 text-sm space-y-1 border border-border">
                    <p className="text-muted-foreground">
                      Current balance: <strong className="text-foreground">{formatCurrency(balancePreview.current_balance, balancePreview.currency)}</strong>
                    </p>
                    <p className="text-muted-foreground">
                      Balance after payment: <strong className="text-emerald-600">{formatCurrency(balancePreview.suggested_balance, balancePreview.currency)}</strong>
                    </p>
                  </div>
                )}

                {paymentForm.deposit_account_id && balancePreview && (
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Confirm actual balance after (optional)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder={balancePreview ? String(balancePreview.suggested_balance) : "Actual balance after payment"}
                      value={paymentForm.user_confirmed_balance_after}
                      onChange={(e) => setPaymentForm((p) => ({ ...p, user_confirmed_balance_after: e.target.value }))}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPaymentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddPayment}>Record Payment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time Entries Dialog */}
      <Dialog open={showTimeEntriesDialog} onOpenChange={setShowTimeEntriesDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Add Time Entries to Invoice</DialogTitle>
            <DialogDescription>
              Select billable time entries to add (Rate: {formatCurrency(client?.default_rate || 0, currency)}/hr)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {unbilledEntries.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No unbilled time entries</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12"></TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {unbilledEntries.map((entry) => {
                    const isSelected = selectedTimeEntries.some(e => e.id === entry.id);
                    const amount = (entry.duration_minutes / 60) * (client?.default_rate || 0);
                    return (
                      <TableRow key={entry.id}>
                        <TableCell>
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={(checked) => {
                              if (checked) {
                                setSelectedTimeEntries([...selectedTimeEntries, entry]);
                              } else {
                                setSelectedTimeEntries(selectedTimeEntries.filter(e => e.id !== entry.id));
                              }
                            }}
                          />
                        </TableCell>
                        <TableCell>{formatDate(entry.start_time)}</TableCell>
                        <TableCell>{entry.description || "-"}</TableCell>
                        <TableCell className="font-mono">{formatDuration(entry.duration_minutes)}</TableCell>
                        <TableCell className="font-mono">{formatCurrency(amount, currency)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTimeEntriesDialog(false)}>Cancel</Button>
            <Button onClick={handleAddTimeEntries} disabled={selectedTimeEntries.length === 0}>
              Add Selected ({selectedTimeEntries.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Dialog */}
      <Dialog open={showCommissionDialog} onOpenChange={setShowCommissionDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add Commission</DialogTitle>
            <DialogDescription>Calculate commission from a deal or sale</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Description</Label>
              <Input
                placeholder="e.g., Commission — Project X sale"
                value={commissionForm.description}
                onChange={(e) => setCommissionForm({ ...commissionForm, description: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Base Amount (deal/sale value)</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={commissionForm.base_amount}
                onChange={(e) => setCommissionForm({ ...commissionForm, base_amount: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Commission %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={commissionForm.commission_pct}
                onChange={(e) => setCommissionForm({ ...commissionForm, commission_pct: e.target.value })}
              />
            </div>
            {commissionForm.base_amount && commissionForm.commission_pct && (
              <p className="text-sm text-muted-foreground">
                Commission amount:{" "}
                <span className="font-mono font-semibold">
                  {formatCurrency(
                    Math.round((parseFloat(commissionForm.base_amount) || 0) * ((parseFloat(commissionForm.commission_pct) || 0) / 100) * 100) / 100,
                    currency
                  )}
                </span>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionDialog(false)}>Cancel</Button>
            <Button onClick={handleAddCommission}>Add Commission</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
